/**
 * Token Encryption Service
 * 
 * Provides secure token encryption/decryption using Web Crypto API (AES-GCM)
 * Tokens are encrypted and stored in localStorage for persistence across page refreshes
 */

const SALT = 'mf-admin-portal-salt-v1'; // Salt for key derivation
const KEY_ITERATIONS = 100000; // PBKDF2 iterations
const STORAGE_KEY_ACCESS = 'encrypted_access_token';
const STORAGE_KEY_REFRESH = 'encrypted_refresh_token';

class TokenEncryptionService {
  private encryptionKey: CryptoKey | null = null;
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize the encryption service by deriving the encryption key
   */
  private async initialize(): Promise<void> {
    if (this.encryptionKey) {
      return; // Already initialized
    }

    // Prevent multiple concurrent initializations
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.deriveKey();
    await this.initPromise;
    this.initPromise = null;
  }

  /**
   * Derive encryption key using PBKDF2 from device-specific identifier
   * Uses a combination of user agent and a stable identifier
   */
  private async deriveKey(): Promise<void> {
    try {
      // Create a device-specific identifier
      // Using navigator.userAgent + a stable identifier stored in localStorage
      let deviceId = localStorage.getItem('mf_device_id');
      if (!deviceId) {
        // Generate a random device ID on first use
        deviceId = this.generateDeviceId();
        localStorage.setItem('mf_device_id', deviceId);
      }

      const password = `${navigator.userAgent}-${deviceId}`;
      
      // Convert password to key material
      const encoder = new TextEncoder();
      const passwordBuffer = encoder.encode(password);
      const keyMaterial = await crypto.subtle.importKey(
        'raw',
        passwordBuffer,
        { name: 'PBKDF2' },
        false,
        ['deriveBits', 'deriveKey']
      );

      // Derive AES-GCM key
      const saltBuffer = encoder.encode(SALT);
      this.encryptionKey = await crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt: saltBuffer,
          iterations: KEY_ITERATIONS,
          hash: 'SHA-256',
        },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
      );

      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 Token encryption key derived successfully');
      }
    } catch (error) {
      console.error('Failed to derive encryption key:', error);
      throw error;
    }
  }

  /**
   * Generate a unique device identifier
   */
  private generateDeviceId(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Encrypt a token string
   * @param token - The plaintext token to encrypt
   * @returns Base64-encoded encrypted token with IV prepended
   */
  async encryptToken(token: string): Promise<string> {
    try {
      await this.initialize();

      if (!this.encryptionKey) {
        throw new Error('Encryption key not available');
      }

      // Generate random IV (Initialization Vector)
      const iv = crypto.getRandomValues(new Uint8Array(12));

      // Encrypt the token
      const encoder = new TextEncoder();
      const tokenBuffer = encoder.encode(token);
      const encryptedBuffer = await crypto.subtle.encrypt(
        {
          name: 'AES-GCM',
          iv: iv,
        },
        this.encryptionKey,
        tokenBuffer
      );

      // Combine IV + encrypted data
      const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
      combined.set(iv, 0);
      combined.set(new Uint8Array(encryptedBuffer), iv.length);

      // Convert to base64
      return this.arrayBufferToBase64(combined);
    } catch (error) {
      console.error('Token encryption failed:', error);
      throw error;
    }
  }

  /**
   * Decrypt a token string
   * @param encryptedToken - Base64-encoded encrypted token with IV prepended
   * @returns Decrypted plaintext token
   */
  async decryptToken(encryptedToken: string): Promise<string> {
    try {
      await this.initialize();

      if (!this.encryptionKey) {
        throw new Error('Encryption key not available');
      }

      // Convert from base64
      const combined = this.base64ToArrayBuffer(encryptedToken);

      // Extract IV and encrypted data
      const iv = combined.slice(0, 12);
      const encryptedData = combined.slice(12);

      // Decrypt
      const decryptedBuffer = await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: iv,
        },
        this.encryptionKey,
        encryptedData
      );

      // Convert to string
      const decoder = new TextDecoder();
      return decoder.decode(decryptedBuffer);
    } catch (error) {
      console.error('Token decryption failed:', error);
      throw error;
    }
  }

  /**
   * Store encrypted access token in localStorage
   */
  async storeAccessToken(token: string): Promise<void> {
    try {
      const encrypted = await this.encryptToken(token);
      localStorage.setItem(STORAGE_KEY_ACCESS, encrypted);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 Access token encrypted and stored');
      }
    } catch (error) {
      console.error('Failed to store encrypted access token:', error);
      // Fail silently - token will remain in memory only
    }
  }

  /**
   * Store encrypted refresh token in localStorage
   */
  async storeRefreshToken(token: string): Promise<void> {
    try {
      const encrypted = await this.encryptToken(token);
      localStorage.setItem(STORAGE_KEY_REFRESH, encrypted);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 Refresh token encrypted and stored');
      }
    } catch (error) {
      console.error('Failed to store encrypted refresh token:', error);
      // Fail silently - token will remain in memory only
    }
  }

  /**
   * Retrieve and decrypt access token from localStorage
   */
  async getAccessToken(): Promise<string | null> {
    try {
      const encrypted = localStorage.getItem(STORAGE_KEY_ACCESS);
      if (!encrypted) {
        return null;
      }
      
      return await this.decryptToken(encrypted);
    } catch (error) {
      console.error('Failed to retrieve encrypted access token:', error);
      // Clear corrupted token
      localStorage.removeItem(STORAGE_KEY_ACCESS);
      return null;
    }
  }

  /**
   * Retrieve and decrypt refresh token from localStorage
   */
  async getRefreshToken(): Promise<string | null> {
    try {
      const encrypted = localStorage.getItem(STORAGE_KEY_REFRESH);
      if (!encrypted) {
        return null;
      }
      
      return await this.decryptToken(encrypted);
    } catch (error) {
      console.error('Failed to retrieve encrypted refresh token:', error);
      // Clear corrupted token
      localStorage.removeItem(STORAGE_KEY_REFRESH);
      return null;
    }
  }

  /**
   * Clear all encrypted tokens from localStorage
   */
  clearTokens(): void {
    localStorage.removeItem(STORAGE_KEY_ACCESS);
    localStorage.removeItem(STORAGE_KEY_REFRESH);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔐 Encrypted tokens cleared from storage');
    }
  }

  /**
   * Convert ArrayBuffer to Base64 string
   */
  private arrayBufferToBase64(buffer: Uint8Array): string {
    let binary = '';
    const len = buffer.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(buffer[i]);
    }
    return btoa(binary);
  }

  /**
   * Convert Base64 string to ArrayBuffer
   */
  private base64ToArrayBuffer(base64: string): Uint8Array {
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}

export const tokenEncryptionService = new TokenEncryptionService();
export default tokenEncryptionService;

